import { cva, type VariantProps } from "class-variance-authority";
import {
	Children,
	type FC,
	type HTMLAttributes,
	isValidElement,
	type ReactNode,
	type Ref,
} from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { AvatarOverflowBadge, type AvatarSize, AvatarSizeContext, useAvatarSize } from "./Avatar";

/** Avatars beyond this count collapse into a `+N` badge. */
const DEFAULT_MAX_VISIBLE = 4;

/** At this many avatars the stack tightens at rest and spreads on group hover. */
const DEFAULT_SPREAD_THRESHOLD = 4;

export const avatarGroupVariants = cva("group flex w-fit items-center", {
	variants: {
		anchor: {
			left: "",
			right: "ml-auto",
		},
	},
	defaultVariants: { anchor: "left" },
});

/**
 * Overlap between an avatar and the one before it. Below `spreadThreshold` avatars they sit at a
 * fixed 6px overlap; at or past it they tighten to 10px and spread back to 6px on group hover.
 */
const stackOffsetVariants = cva("", {
	variants: {
		spread: {
			true: "-ml-[10px] transition-[margin] duration-150 ease-out group-hover:-ml-[6px]",
			false: "-ml-[6px]",
		},
	},
	defaultVariants: { spread: false },
});

type AvatarGroupVariantProps = VariantProps<typeof avatarGroupVariants>;

export type AvatarGroupAnchor = NonNullable<AvatarGroupVariantProps["anchor"]>;

export type AvatarGroupProps = HTMLAttributes<HTMLDivElement> & {
	/**
	 * Edge of the container the group is pinned to. Both values read left to right with the leftmost
	 * avatar on top — `right` only moves the group itself, so it grows leftward as avatars are added.
	 * @default "left"
	 */
	anchor?: AvatarGroupAnchor;

	/** `Avatar` elements to stack, left to right. */
	children?: ReactNode;

	/**
	 * Total circles rendered before collapsing. Past this count the group shows `max - 1`
	 * avatars plus a `+N` badge. At least one avatar always stays visible, so `max={1}` renders
	 * one avatar plus the badge rather than the badge alone.
	 * @default 4
	 */
	max?: number;

	/** Diameter applied to every avatar in the group, badge included. @default "md" */
	size?: AvatarSize;

	/**
	 * Avatar count at which the stack tightens at rest and spreads back apart on group hover.
	 * Below it the overlap stays fixed.
	 * @default 4
	 */
	spreadThreshold?: number;

	/** Extra classes on the `+N` badge, so it can match a tinted avatar fill. */
	overflowClassName?: string;

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	ref?: Ref<HTMLDivElement>;
};

/**
 * AvatarGroup primitive. Stacks `Avatar` children left to right with the leftmost on top,
 * collapsing anything past `max` into a trailing `+N` badge. Hovering the group spreads the
 * avatars apart so each one is legible. `anchor="right"` pins the group to the right edge of its
 * container so it grows leftward, without changing the left-to-right reading order.
 */
export const AvatarGroup: FC<AvatarGroupProps> = ({
	anchor = "left",
	children,
	max = DEFAULT_MAX_VISIBLE,
	size,
	spreadThreshold = DEFAULT_SPREAD_THRESHOLD,
	overflowClassName,
	trackingId,
	className,
	ref,
	...rest
}) => {
	const resolvedSize = useAvatarSize(size);
	const avatars = Children.toArray(children).filter(isValidElement);

	const isOverflowing = avatars.length > max;
	const visibleAvatars = isOverflowing ? avatars.slice(0, Math.max(1, max - 1)) : avatars;
	const overflowCount = avatars.length - visibleAvatars.length;
	const stackOffsetClasses = stackOffsetVariants({
		spread: avatars.length >= spreadThreshold,
	});

	return (
		<div
			ref={ref}
			className={cn(avatarGroupVariants({ anchor }), className)}
			{...trackingAttrs(trackingId)}
			{...rest}
		>
			<AvatarSizeContext.Provider value={resolvedSize}>
				{visibleAvatars.map((avatar, index) => (
					<div
						key={avatar.key}
						className={cn("relative", index > 0 && stackOffsetClasses)}
						style={{ zIndex: visibleAvatars.length - index }}
					>
						{avatar}
					</div>
				))}
				{overflowCount > 0 && (
					<div className={cn("relative z-0", stackOffsetClasses)}>
						<AvatarOverflowBadge
							count={overflowCount}
							size={resolvedSize}
							className={overflowClassName}
						/>
					</div>
				)}
			</AvatarSizeContext.Provider>
		</div>
	);
};
AvatarGroup.displayName = "AvatarGroup";
